import React from 'react';
import {
    FilterDatatable,
    FiltersDatatableList,
    SelectApiFilter,
    StaticSelectFilter,
} from '@europrocurement/flexy-datatable';
import { Box, Typography, useTheme } from '@mui/material';
import useFactureApiHook, {
    FactureType,
    LimitAccessFilterApiMultipleFactureOpenSearch,
} from './useFactureApiHook';

export enum FactureStatus {
    TO_PAY = 'TO_PAY',
    PAYED = 'PAYED',
    LATE = 'LATE',
}

export enum FactureTypeEnum {
    INVOICE = 'INVOICE',
    CREDIT = 'CREDIT_NOTE',
    INVOICE_WITH_CREDIT = 'INVOICE_WITH_CREDIT',
}

type FactureDossierCollection = {
    collectionNumero: string;
    collectionId: string;
    childsCount: string;
};

const useFactureFilters = ({
    prescriberIdRef,
    ddmIdFilter,
}: LimitAccessFilterApiMultipleFactureOpenSearch) => {
    const { palette } = useTheme();
    const {
        searchFactureSocieteFacturee,
        searchFactureDossier,
        searchFactureNumero,
        searchFactureReference,
        searchFacturePrescriber,
        searchFactureCollectionDossier,
    } = useFactureApiHook({ prescriberIdRef, ddmIdFilter });

    const prescripteurFilter: FilterDatatable = {
        label: 'Prescripteur',
        field: 'prescriberId',
        type: 'selectApi',
        icon: 'user',
        searchFunction: searchFacturePrescriber,
        renderItem: (item: FactureType) => item.libelle as string,
        valueMap: (item: FactureType) => item.id as number,
    };

    const societeFactureeFilter: SelectApiFilter<FactureType> = {
        label: 'Société facturée',
        field: 'clientId',
        type: 'selectApi',
        icon: 'building-user',
        searchFunction: searchFactureSocieteFacturee,
        renderItem: (item: FactureType) => item.libelle as string,
        valueMap: (item: FactureType) => item.id as number,
        showResultOnSearchOnly: true,
    };

    const numeroFactureFilter: FilterDatatable = {
        label: 'Numéro de facture',
        field: 'factureNumero',
        type: 'selectApi',
        icon: 'hashtag',
        searchFunction: searchFactureNumero,
        renderItem: (item: FactureType) => item.factureNumero as string,
        valueMap: (item: FactureType) => item.factureNumero as string,
    };

    const numeroDossierFilter: FilterDatatable = {
        label: 'Numéro de dossier',
        field: 'dossierNumero',
        type: 'selectApi',
        icon: 'file-lines',
        searchFunction: searchFactureDossier,
        renderItem: (item: FactureType) => item.dossierNumero as string,
        valueMap: (item: FactureType) => item.dossierNumero as string,
    };

    const referenceClientFilter: FilterDatatable = {
        label: 'Réf client',
        field: 'referenceClient',
        type: 'selectApi',
        icon: 'user',
        searchFunction: searchFactureReference,
        renderItem: (item: FactureType) => item.reference as string,
        valueMap: (item: FactureType) => item.reference as string,
    };

    const dateFactureFilter: FilterDatatable = {
        type: 'dateRange',
        label: 'Date Facture',
        field: 'dateFacture',
        icon: 'calendar',
        displayFormat: (startDate: Date, endDate: Date) =>
            `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
    };

    const dateEcheanceFilter: FilterDatatable = {
        type: 'dateRange',
        label: 'Échéance',
        field: 'dateEcheanceFacture',
        icon: 'calendar',
        displayFormat: (startDate: Date, endDate: Date) =>
            `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
    };

    const totalTTCFilter: FilterDatatable = {
        type: 'number',
        label: 'Total TTC',
        field: 'totalTTC',
        icon: 'euro-sign',
    };

    const statusFilter: StaticSelectFilter = {
        field: 'status',
        label: 'Statut',
        type: 'staticSelect',
        options: [
            {
                value: FactureStatus.TO_PAY,
                label: 'À régler',
            },
            {
                value: FactureStatus.PAYED,
                label: 'Réglée',
            },
            {
                value: FactureStatus.LATE,
                label: 'En retard',
            },
        ],
    };

    const demoFilter: FilterDatatable = {
        label: 'Démo',
        field: 'demo',
        icon: 'flask',
        type: 'boolean',
        hidden: true,
    };
    const enPreparationFilter: FilterDatatable = {
        label: 'En Preparation',
        field: 'enPreparation',
        icon: 'file-pen',
        type: 'boolean',
        hidden: true,
    };

    const typeFactureFilter: StaticSelectFilter = {
        field: 'type',
        label: 'Type',
        type: 'staticSelect',
        icon: 'file',
        options: [
            {
                value: FactureTypeEnum.INVOICE,
                label: 'facture',
            },
            {
                value: FactureTypeEnum.CREDIT,
                label: 'avoir',
            },
        ],
    };

    const numeroCollectionDossierFilter: FilterDatatable = {
        label: 'Commande',
        field: 'collectionId',
        type: 'selectApi',
        icon: 'link-horizontal',
        searchFunction: searchFactureCollectionDossier,
        renderItem: (item: FactureDossierCollection) => (
            <Box
                sx={{
                    display: 'flex',
                    gap: '6px',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography sx={{ fontSize: '14px', fontWeight: '500' }}>
                    {item.collectionNumero}
                </Typography>
                <Box
                    sx={{
                        borderRadius: '100%',
                        border: `1px solid ${palette.grey[700]}`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '1rem',
                        height: '1rem',
                        color: palette.grey[700],
                        fontSize: '0.7rem',
                    }}
                >
                    {item.childsCount}
                </Box>
            </Box>
        ),
        valueMap: (item: FactureType) => item.collectionId as string,
        showResultOnSearchOnly: true,
    };

    const factureFiltersFront: FiltersDatatableList = [
        societeFactureeFilter,
        numeroFactureFilter,
        numeroDossierFilter,
        referenceClientFilter,
        dateFactureFilter,
        dateEcheanceFilter,
        totalTTCFilter,
        typeFactureFilter,
        numeroCollectionDossierFilter,
    ];

    const formalitiesInvoicesFiltersBack: FiltersDatatableList = [
        prescripteurFilter,
        societeFactureeFilter,
        numeroFactureFilter,
        numeroDossierFilter,
        referenceClientFilter,
        dateFactureFilter,
        dateEcheanceFilter,
        statusFilter,
        demoFilter,
        enPreparationFilter,
        {
            // Add extra invoice type for the back
            ...typeFactureFilter,
            options: [
                ...typeFactureFilter.options,
                {
                    value: FactureTypeEnum.INVOICE_WITH_CREDIT,
                    label: 'factures avec avoirs',
                },
            ],
        },
        numeroCollectionDossierFilter,
    ];

    return { factureFiltersFront, formalitiesInvoicesFiltersBack };
};

export default useFactureFilters;
